import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';

import { MATERIAL_TYPE, UnitType } from 'libs/constants';
import { FluidModel, ISAPMaterialModel, PumpScheduleStageMaterialModel } from 'libs/models';
import { EventHubService, FluidService, UpdateMaterialParameters } from 'libs/shared';
import { ErrorMessageModel } from 'libs/ui/validations/error-message.model';
import { UnitConversionService } from '../../../../../../../libs/ui';
import { EditJobAdapter } from '../../../edit-job/adapters';
import { ControlPointType, ValidatorSetting } from '../../../shared/constant';
import { MaterialNumberAssignedFlag } from '../../../shared/constant/slurry-source';
import { FluidStateManager } from '../../state/fluid-state-manager';
import { FluidMaterialStateManager } from '../../state/material-state-manager';
import { StageStateManager } from '../../state/stage-state-manager';
import { ViewState } from '../../view-state';
import { PumpScheduleStateFactory } from '../../state/state-factory';
import { ControlPointAdapter } from '../../../control-point/adapters/control-point.adapter';
import { cloneDeep, isNil } from 'lodash'; 

@Component({
    selector: 'stage-fluid-material',
    templateUrl: './stage-fluid-material.component.html',
    styleUrls: ['./stage-fluid-material.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StageFluidMaterialComponent implements OnInit, OnDestroy, AfterViewInit {
  private weight;
  private volume;
  private static _volumeFields: string[] = [
    'loadoutVolume',
    'overrideVolume',
  ];

  private static _cp1Fields: string[] = [
    'concentration',
    'lotNo',
    'sapMaterialNumber',
    'totalCOGS',
    'overrideMixingProcedure',
    'displayName'
  ];

  private static _cp2Fields: string[] = [
    'concentration',
    'lotNo',
    ...StageFluidMaterialComponent._volumeFields,
    'dryVolume',
    'overrideMixingProcedure',
    'displayName'
  ];

  private static _cp4Fields: string[] = [
    ...StageFluidMaterialComponent._volumeFields,
    'overrideMixingProcedure',
    'actualQty'
  ];

  private static _scheduleEditFields: string[] = [
    ...StageFluidMaterialComponent._volumeFields,
    'overrideMixingProcedure',
    'concentration',
    'lotNo',
    'dryVolume',
    'totalCOGS',
    'displayName'
  ];

  // array index corresponds to cp number,
  // if 0 - this is schedule edit view
  private static _visibleFields: string[][] = [
    StageFluidMaterialComponent._scheduleEditFields,
    StageFluidMaterialComponent._cp1Fields,
    StageFluidMaterialComponent._cp2Fields,
    [],
    StageFluidMaterialComponent._cp4Fields
  ];
  @Input() volumeUpdates: Observable<{
    volume: number,
    eventOrder: number,
    stageOrder: number
  }>;
  @Input()
  public stageState: StageStateManager;

  @Input()
  public fluidState: FluidStateManager;

  @Input()
  public readonly materials: PumpScheduleStageMaterialModel[];

  @Input()
  public readonly header: string;

  @Input()
  public readonly showMix: boolean;

  @Input()
  public readonly refreshForm: Observable<any>;

  @Input()
  public readonly fluidType: string;


  public isTableExpanded: boolean = false;

  public UnitType = UnitType;

  public errorMessages = {
    sapMaterialNumber: [
      new ErrorMessageModel('notExisted', 'SAP Number does not exist. Please input another value.')
    ],
    overrideVolume: [
      new ErrorMessageModel('negativeNumber', ValidatorSetting.ERROR_NEGATIVE),
      new ErrorMessageModel('isNumber', ValidatorSetting.ERROR_NUMBER_NUMBER),
      new ErrorMessageModel('max', ValidatorSetting.ERROR_UNIT_DECIMAL_MAX)
    ]
  };

  private readonly _subscriptions = new Subscription();
  private _currentFluid: FluidModel = null;
  private stageStateFluids = [];

  hideMaterialTemplate = false;

  public constructor(
    private fluidService: FluidService,
    private readonly _editJobAdapter: EditJobAdapter,
    private cd: ChangeDetectorRef,
    private unitConversionService: UnitConversionService,
    private readonly _scheduleStateFactory: PumpScheduleStateFactory,
    public controlPointAdapter: ControlPointAdapter,
    public eventHub: EventHubService
  ) {
  }

  ngAfterViewInit() {
    this.eventHub.onMixUpdate$.subscribe(x => {
      this.getForm(x).patchValue({ overrideMixingProcedureId: x.overrideMixingProcedureId })
      this.cd.markForCheck();
    });
  }

  public get isBlend(): boolean {
    return this.fluidType == MATERIAL_TYPE.BLEND && this.stageState._model.slurry.blendName != null;
  }
  private get _viewState(): ViewState {

    return this.fluidState.viewState;
  }

  public get isOffshoreJob$(): Observable<boolean> {

    return this._viewState.isOffshoreJob$;
  }

  public get isExistingJob(): boolean {

    return this._viewState.isExistingJob;
  }

  public get isOverrideVolumeEditable(): boolean {

    return !this._viewState.isCP4View;
  }

  public get isSapMaterialEditable(): boolean {

    return !this._viewState.isCP4View;
  }

  public get isMixEditable(): boolean {

    return !(this._viewState.isCP4View || this._viewState.isCP2Completed) && this._viewState.isJobEditable;
  }

  public get enablingCPStates$(): Observable<boolean> {

    return this._viewState.isCP1Submitted$
      .pipe(
        map(isCP1Submitted => {

          return !this._viewState.isJobCompleted
            && (isCP1Submitted || this._viewState.isCP2Approved)
            && !this._viewState.isCP2Completed
            && !this._viewState.isCP4Completed;
        }),
        shareReplay()
      );
  }

  public get disablingJobStates(): boolean {

    return this._viewState.isJobCancelled || this._viewState.isJobClosed;
  }

  public get isCP4QtyDisabled(): boolean {
    return this._viewState.controlPointNumber === 4;
  }

  public get disablingCPStates(): boolean {

    return this.disablingJobStates || this._viewState.isCP2Completed;
  }
  public get classOnCPState$(): Observable<string> {

    return this.enablingCPStates$
      .pipe(
        map(enablingCPStates => {

          if (this.disablingCPStates) {

            return 'custom-disable';

          } else if (enablingCPStates) {

            return 'exception-disable-item';
          }

          return '';

        }),
        shareReplay()
      );
  }

  public get classOnCP2State$(): Observable<string> {

    return this.enablingCPStates$
      .pipe(
        map(enablingCPStates => {

          if (this.disablingCPStates || this._viewState.isCP2Prepared) {

            return 'custom-disable';

          } else if (enablingCPStates) {

            return 'exception-disable-item';
          }

          return '';

        }),
        shareReplay()
      );
  }
  public updateLoadOutVolume() {
    this.materials.forEach(material => {
      const materialState = this.fluidState.getMaterialState(material);
      if (materialState) {
        materialState.initializeLoadoutQuantity();
      }
    });
  }
  public ngOnInit(): void {
    this._subscriptions.add(
      this.volumeUpdates?.subscribe(() => { 
        if (this.materials) { 
          this.updateLoadOutVolume();
        } 
        this.cd.markForCheck(); 
      })
    );
    this._subscriptions.add(
      this.fluidState.form.get('loadoutVolume')?.valueChanges.subscribe(initialLoadout => {
        if (initialLoadout !== null && initialLoadout !== undefined) {
          if (this.materials) {
            this.updateLoadOutVolume();
          }
        }
      })
    );
    this.weight = this.unitConversionService.getCurrentUnitMeasure(UnitType.Weight);
    this.volume = this.unitConversionService.getCurrentUnitMeasure(UnitType.LargeVolume);
    this._subscriptions.add(
      this.fluidState.model$
        .pipe(
          switchMap(fluid => {

            this._currentFluid = fluid;

            return this._editJobAdapter.updateSAPMaterial$
              .pipe(
                take(1),
                map(updateData => {
                  return {
                    fluid: fluid,
                    materialData: updateData
                  };
                })
              );
          })
        )
        .subscribe(({ fluid, materialData }) => {
          this._updateMappingMaterial(fluid, materialData)
        })
    );
    this._subscriptions.add(
      this.stageState.availableFluids$.subscribe(fluids => {
        this.stageStateFluids = fluids;
      })
    );
    this._subscriptions.add(this.fluidService.materialMappingUpdated$.subscribe(() => this.cd.markForCheck()));
    this._subscriptions.add(this.fluidService.updateMaterial$.subscribe(
      (values: UpdateMaterialParameters) => {
        const updMaterial = values.material;
        if (!updMaterial || (updMaterial.fluidMaterialId == null && updMaterial.fluidMaterialName == null))
          return;

        if (!isNil(this.materials)) {
          for (const m of this.materials) {
            if (this._materialMatchesUpdate(m, updMaterial)) {
              m.sapMaterialNumber = updMaterial.sapMaterialNumber;
              m.sapMaterialName = updMaterial.sapMaterialName;
              m.sapMaterialDisplayName = updMaterial.sapMaterialDisplayName;
              this.getSapMaterialNumberControl(updMaterial).setValue(updMaterial.sapMaterialNumber);
              this.getSapMaterialNameControl(updMaterial).setValue(updMaterial.sapMaterialName);
              this._updateFluidsTabSapMaterial(updMaterial, values.sapMaterial); 
            }
          }
        }
        this.cd.markForCheck();
      }
    ));


    if (this.refreshForm != null) {
      this.refreshForm.subscribe(() => {
        this.cd.detectChanges();
      })
    }
  }

  public ngOnDestroy(): void {

    this._subscriptions.unsubscribe();
  }

  public isVisible$(field: string): Observable<boolean> {

    let mixFieldVisible = true;
    if (field === 'overrideMixingProcedure') {

      mixFieldVisible = this.showMix;
    }

    return this.fluidState.viewState.isFieldVisible$(
      field,
      StageFluidMaterialComponent._visibleFields
    )
      .pipe(
        map(visible => {

          return visible && mixFieldVisible;
        })
      );
  }

  public getDropdownMixItems$(material: PumpScheduleStageMaterialModel)
    : Observable<SelectItem<string>[]> {

    return this._getMaterialState(material).dropdownMixItems$;
  }

  public toggleExpanded(): void {

    this.isTableExpanded = !this.isTableExpanded;
  }

  public getForm(material: PumpScheduleStageMaterialModel): UntypedFormGroup {

    return this._getMaterialState(material).form;
  }

  public getIFactsMaterialIdControl(material: PumpScheduleStageMaterialModel): AbstractControl {

    return this.getForm(material).controls.ifactMaterialId;
  }

  public getSapMaterialNumberControl(material: PumpScheduleStageMaterialModel): AbstractControl {

    return this.getForm(material).controls.sapMaterialNumber;
  }

  public getSapMaterialNameControl(material: PumpScheduleStageMaterialModel): AbstractControl {

    return this.getForm(material).controls.sapMaterialName;
  }

  public isMixWater(material: PumpScheduleStageMaterialModel): boolean {

    return this._getMaterialState(material).isMixWater;
  }

  public isDry(material: PumpScheduleStageMaterialModel): boolean {

    return this._getMaterialState(material).isDry;
  }

  public isDryVolumeAllowedByMixMethod(material: PumpScheduleStageMaterialModel): boolean {
    return material.overrideMixingProcedureId != PumpScheduleStateFactory.iFactsMaterialMixProceduresMap.PH
      && material.overrideMixingProcedureId != PumpScheduleStateFactory.iFactsMaterialMixProceduresMap.PM;
  }

  public isLiquidUnit = (material: PumpScheduleStageMaterialModel): boolean => this._getMaterialState(material).isLiquidUnit;

  public cogsUomIncompatible$(material: PumpScheduleStageMaterialModel): Observable<boolean> {

    return this._getMaterialState(material).cogsUomIncompatible$;
  }

  public cogsAvailable$(material: PumpScheduleStageMaterialModel): Observable<boolean> {

    return this._getMaterialState(material).cogsAvailable$;
  }

  public getCogs$(material: PumpScheduleStageMaterialModel): Observable<number> {

    return this._getMaterialState(material).cogs$
      .pipe(
        map(cost => {
          if(cost != null && cost!= undefined){
          const updatedCogs = [...this.controlPointAdapter.totalCOGSList$.value || [], cost]           
                              .filter((item, index, self) => index === self.findIndex(t => t.id === item.id));
            this.controlPointAdapter.totalCOGSList$.next(updatedCogs);
          }
          return cost.totalCOGS;
        }),
        shareReplay()
      );
  }

  public bulkDensityAvailable$(material: PumpScheduleStageMaterialModel): Observable<boolean> {

    return this._getMaterialState(material).bulkDensityAvailable$;
  }

  public updateSapMaterial(material: PumpScheduleStageMaterialModel, sapMaterial: ISAPMaterialModel): void {
    this._getMaterialState(material).updateSap(sapMaterial);
  }

  public onUpdateSapMaterial(material: PumpScheduleStageMaterialModel, sapMaterial: ISAPMaterialModel, state = null, hideMaterialTemplate = false, stageState: StageStateManager = null): void {
    this.hideMaterialTemplate = hideMaterialTemplate;
    if (state !== null) {
      this.fluidState = state;
      this.stageState = stageState;
    }

    const mat = cloneDeep(material);
    this.eventHub.onChangedSAP$.next(mat);

    this._getMaterialState(material).updateSapManually(sapMaterial, material);
    this.fluidService.updateMaterial$.next({ material, sapMaterial });
    this.updateFluidInAllStages(true);
  }

  onMixChanged(material: any) {
    this.eventHub.onMixChanged$.next(material);
  }

  private updateFluidInAllStages(skipResetForm: boolean = false) {
    this.fluidState.model$.subscribe(model =>
      this._scheduleStateFactory.updateFluidInAllStages(model.requestId, model.slurryNo, model, skipResetForm)
    );
  }

  public isCPviewed(checkCP: ControlPointType = null): boolean {

    const currentCP = this.fluidState.viewState.currentCPinView;

    if (!checkCP)
      return !!checkCP === !!currentCP;

    return checkCP === currentCP;
  }

  private _getMaterialState(material: PumpScheduleStageMaterialModel): FluidMaterialStateManager {

    return this.fluidState.getMaterialState(material);
  }

  overrideVolumeControlBlur(material: PumpScheduleStageMaterialModel) {
    const value = this.getForm(material).get('overrideVolume')?.getRawValue();
    this._getMaterialState(material).overrideVolumeControlBlur$.next(value);
  }

  private _updateFluidsTabSapMaterial(material: PumpScheduleStageMaterialModel, sapMaterial: ISAPMaterialModel): void {

    const slurries = this._editJobAdapter.fluidFormArray.controls;

    if (!slurries || !this._currentFluid || !this.stageStateFluids.length) { return; }

    slurries.forEach((slurry: UntypedFormGroup, index: number) => {

      let stageStateFluid = this.stageStateFluids[index];
// eslint-disable-next-line
      if (!!slurry.value?.requestId) {
        const stageStateFluidLookup = this.stageStateFluids.find(x => x.requestId == slurry.value.requestId);
        // eslint-disable-next-line
        if (!!stageStateFluidLookup) {
          stageStateFluid = stageStateFluidLookup;
        }
      }

      const isSuppelemtalsGroup = this.materials.every(m => this._getMaterialState(m).isSupplemental);
      const isBlendGroup = this.materials.every(m => this._getMaterialState(m).isBlend);
      let fluidMaterials;
      let fluidMaterialForm;

      if (isSuppelemtalsGroup) {
        fluidMaterials = stageStateFluid.supplementalMaterial;
        fluidMaterialForm = slurry.controls.supplementalMaterial as UntypedFormArray;
      } else if (isBlendGroup) {
        fluidMaterials = stageStateFluid.fluidBlendMaterial;
        fluidMaterialForm = slurry.controls.fluidBlendMaterial as UntypedFormArray;
      } else if (!isBlendGroup) {
        fluidMaterials = stageStateFluid.fluidAdditiveMaterial;
        fluidMaterialForm = slurry.controls.fluidAdditiveMaterial as UntypedFormArray;
      }
      this._setFluidMaterial(material, sapMaterial, fluidMaterialForm, fluidMaterials, this._currentFluid.requestDisplay);
    });
  }

  private _setFluidMaterial(
    material: PumpScheduleStageMaterialModel,
    sapMaterial: ISAPMaterialModel,
    materialFormArray: UntypedFormArray,
    fluidMaterials,
    requestDisplay: string
  ) {

    if (!fluidMaterials || !materialFormArray || !sapMaterial) { return; }

    fluidMaterials.forEach((fluidMaterial, index) => {

      if (fluidMaterial.materialId !== material.ifactMaterialId) { return; }

      const fluidMaterialForm = materialFormArray.controls[index] as UntypedFormGroup;

      if (fluidMaterialForm?.controls?.sapMaterialName) {

        if (fluidMaterial.id === material.fluidMaterialId || !fluidMaterialForm.controls.sapMaterialName.value) {

          fluidMaterialForm.controls.sapMaterialNumber.setValue(sapMaterial.materialNumber);
          fluidMaterialForm.controls.sapMaterialName.setValue(sapMaterial.materialName);
          fluidMaterialForm.controls.materialNumberAssignedFlag.setValue(MaterialNumberAssignedFlag.Manual);
          fluidMaterialForm.controls.sapMaterialNumberAssignedValue.setValue(requestDisplay);
        }
      }
    });
  }

  private _updateMappingMaterial(fluid: FluidModel, materialData: any): void {
    const materialType = materialData.materialType;
    if (this.fluidType === materialType && fluid && (fluid.id || fluid.slurryIdHDF || fluid.slurryId)) {
      const { id, slurryIdHDF, slurryId, slurryNo } = fluid;
      const slurry = this._editJobAdapter.fluidFormArray.controls
        .find((x: UntypedFormGroup) => (id && x.controls.id.value === id) || (slurryIdHDF && x.controls.slurryIdHDF.value === slurryIdHDF) ||
          (slurryId && x.controls.slurryId.value === slurryId && x.controls.slurryNo.value === slurryNo && slurryNo != null)) as UntypedFormGroup;

      if (slurry) {
        const fluidBlendMaterialFm = slurry.controls.fluidBlendMaterial as UntypedFormArray;
        const fluidAdditiveMaterialFm = slurry.controls.fluidAdditiveMaterial as UntypedFormArray;
        const fluidSupplementalMatFm = slurry.controls.supplementalMaterial as UntypedFormArray;

        switch (materialType) {
          case MATERIAL_TYPE.BLEND:
            this._setStageMaterial(fluidBlendMaterialFm, materialData);
            break;
          case MATERIAL_TYPE.ADDITIVE:
            this._setStageMaterial(fluidAdditiveMaterialFm, materialData);
            break;
          case MATERIAL_TYPE.SUPPLEMENTAL:
            this._setStageMaterial(fluidSupplementalMatFm, materialData);
            break;
        }
      }
    }
    if(this.materials) {
      this.materials.forEach(material => {
        const materialState = this.fluidState.getMaterialState(material);
        if (materialState) {
          materialState.initializeLoadoutQuantity();
        }
      });
    } 
  }

  private _setStageMaterial(fluidMaterialFormsArray: UntypedFormArray, materialData: any) {
    const fluidMaterialForm = fluidMaterialFormsArray.controls.find(x => x.value.materialId === materialData.material.materialId && x.value.materialName === materialData.material.materialName) as UntypedFormGroup;

    let material: PumpScheduleStageMaterialModel;
    if (this.materials.length > materialData.materialRowIndex)
      material = this.materials[materialData.materialRowIndex];

    if (fluidMaterialForm && material) {
      const stageMaterialForm = this.getForm(material);
      const sapMaterialNumber = fluidMaterialForm.controls.sapMaterialNumber.value;

      // Also re-sync when the SAP number is already empty but the display name is still stale
      // (e.g. cleared on CP1 before this material's SAP number control was updated here).
      const displayNameStale = !sapMaterialNumber && !!material.sapMaterialDisplayName;
      if (fluidMaterialForm && fluidMaterialForm.controls && (!this.isEqual(sapMaterialNumber, stageMaterialForm.controls.sapMaterialNumber.value) || displayNameStale)) {
        stageMaterialForm.controls.sapMaterialNumber.setValue(fluidMaterialForm.controls.sapMaterialNumber.value);
        stageMaterialForm.controls.sapMaterialNumber.setErrors(fluidMaterialForm.controls.sapMaterialNumber.errors);

        this.updateSapMaterial(material, {
          materialNumber: stageMaterialForm.controls.sapMaterialNumber.value,
          materialName: fluidMaterialForm.controls.sapMaterialName.value
        });
      }

      if (fluidMaterialForm && fluidMaterialForm.controls && fluidMaterialForm.controls.materialId && fluidMaterialForm.controls.materialName) {
        if (!this.isEqual(stageMaterialForm.controls.ifactMaterialId.value, fluidMaterialForm.controls.materialId.value))
          stageMaterialForm.controls.ifactMaterialId.setValue(fluidMaterialForm.controls.materialId.value);
        if (!this.isEqual(stageMaterialForm.controls.ifactMaterialName.value, fluidMaterialForm.controls.materialName.value))
          stageMaterialForm.controls.ifactMaterialName.setValue(fluidMaterialForm.controls.materialName.value);
      }
    }
  }

  private isEqual = (prevValue: string, newValue: string): boolean => prevValue === newValue;

  // Prefer matching by fluidMaterialId (unique per row). Only fall back to matching by material
  // name when no id is available, otherwise materials sharing the same name across different
  // sections (e.g. an Additive and a Supplemental with the same material) would incorrectly be
  // updated together.
  private _materialMatchesUpdate(material: PumpScheduleStageMaterialModel, updMaterial: any): boolean {
    const matchesById = updMaterial.fluidMaterialId != null && material.fluidMaterialId == updMaterial.fluidMaterialId;
    const matchesByName = updMaterial.fluidMaterialId == null && updMaterial.fluidMaterialName != null
      && material.fluidMaterialName == updMaterial.fluidMaterialName;
    return matchesById || matchesByName;
  }

}
